import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FormGroup, Input, Button, Modal, ComponentLoader, FormInput, Row, Col } from '@snsw/react-component-library';
import styles from './EmailMobileVerification.module.scss';
import EmailMobileVerificationService from './EmailMobileVerificationService';
import Success from './success.svg';

const emailLabels = {
  label: 'Email address*',
  verifyButton: 'Verify',
  codeSent: 'A verification code has been sent to your email address.',
  codeInput: 'Verification code',
  submitButton: 'Submit',
  resendLink: 'Resend code',
  skipVerificationLink: 'Skip verification',
  invalidCode: 'Code is invalid. Please try again',
  verifySuccess: 'Your email address has been verified',
  verifySkip: 'Your email address has not been verified',
  notValid: 'The email address is not valid.',
  notVerified: 'The email address is not verified.',
};

const mobileLabels = {
  label: 'Mobile number*',
  verifyButton: 'Verify',
  codeSent: 'A verification code has been sent to your mobile.',
  codeInput: 'Verification code',
  submitButton: 'Submit',
  resendLink: 'Resend code',
  skipVerificationLink: 'Skip verification',
  invalidCode: 'Code is invalid. Please try again',
  verifySuccess: 'Your mobile number has been verified',
  verifySkip: 'Your mobile number has not been verified',
  notValid: 'The mobile number is not valid.',
  notVerified: 'The mobile number is not verified.',
};

const EmailMobileVerification = (props, ref) => {
  const [config] = useState(props.config);
  const [inputValue, setInputValue] = useState(config?.value || '');
  const [verifiedInputValue, setVerifiedInputValue] = useState(config?.isVerified ? config?.value : '');
  const [inputLabels, setInputLabels] = useState(null);
  const [inputTouched, setInputTouched] = useState(false);
  const [inputType, setInputType] = useState(config.type ? config.type : 'email');
  const [isInputValid, setIsInputValid] = useState(true);
  const [isInputVerified, setIsInputVerified] = useState(config.isVerified ? config.isVerified : false);

  const [inputCode, setInputCode] = useState('');

  const baseUrl = config.baseUrl && config.baseUrl != '' ? config.baseUrl : 'http://localhost:3000';
  const sendCodePath = config.sendCodePath || '/2FA/SendCode';
  const verifyCodePath = config.verifyCodePath || '/2FA/VerifyCode';

  const [sendingCode, setSendingCode] = useState(false);
  const [sendingCodeSuccess, setSendingCodeSuccess] = useState(false);
  const [sendingCodeError, setSendingCodeError] = useState(false);

  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verifyingCodeSuccess, setVerifyingCodeSuccess] = useState(false);
  const [verifyingCodeError, setVerifyingCodeError] = useState(false);

  const errorSendingCode = !sendingCode && sendingCodeError;
  const errorVerifyingCode = !verifyingCode && verifyingCodeError;

  const [isModalOpen, setModalOpen] = useState(false);
  const [isVerifiedOnce, setIsVerifiedOnce] = useState(config?.isVerified ? config?.isVerified : false);

  useEffect(() => {
    setInputLabels(config.type == 'mobile' ? mobileLabels : emailLabels);
  }, [inputType]);

  useImperativeHandle(ref, () => ({
    triggerValidation(callback) {
      let flagValidForCallback = false;

      const isValid = validateInputValue(inputValue);
      if (!isValid) {
        flagValidForCallback = false;
      }

      if (!isVerifiedOnce || !isInputVerified) {
        changeVerificationStatus(false);
        flagValidForCallback = false;
      } else {
        changeVerificationStatus(true);
        flagValidForCallback = true;
      }

      //this will be flag to decide to show error
      setIsVerifiedOnce(true);

      callback(flagValidForCallback);
    },
  }));

  const isValidMobile = (mobile) => {
    // eslint-disable-next-line no-control-regex
    const re =
      /^\({0,1}((0|\+61|61)(2|4|3|7|8)){0,1}\){0,1}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{1}(\ |-){0,1}[0-9]{3}$/i;
    return !(mobile === null || !re.test(String(mobile).toLowerCase()));
  };

  const isValidEmail = (email) => {
    // eslint-disable-next-line no-control-regex
    const re =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
    return !(email === null || !re.test(String(email).toLowerCase()));
  };

  const jsxSendingCodeErrorModal = (
    <Modal
      title={`We were not able to send a verification code to ${inputValue}. Please check that the ${config.type} is correct. If the problem persists, it may be a system error.`}
      onClose={() => setModalOpen(false)}
      buttons={[
        {
          text: 'Close',
          onClick: () => {
            setModalOpen(false);
          },
        },
      ]}
    />
  );

  const jsxVerifyingCodeErrorModal = (
    <Modal
      title={'There was a problem verifying the code. Please verify your input is correct.'}
      onClose={() => setModalOpen(false)}
      buttons={[
        {
          text: 'Close',
          onClick: () => {
            setModalOpen(false);
          },
        },
      ]}
    />
  );

  const changeVerificationStatus = (status) => {
    setIsInputVerified(status);
    if (props.onVerificationChange) {
      props.onVerificationChange(status);
    }
  };
  const buttonsVerifyCodeForm = [
    {
      text: inputLabels ? inputLabels.submitButton : '',
      onClick: () => {
        setVerifyingCode(true);
        setVerifyingCodeSuccess(false);
        setVerifyingCodeError(false);
        EmailMobileVerificationService.verifyCode(baseUrl, verifyCodePath, inputValue, inputCode, config.type)
          .then(() => {
            setVerifyingCode(false);
            setVerifyingCodeSuccess(true);
            setSendingCodeError(false);
            setVerifyingCodeError(false);
            setModalOpen(false);
            changeVerificationStatus(true);
            setVerifiedInputValue(inputValue);
            setIsVerifiedOnce(true);
          })
          .catch(() => {
            setVerifyingCode(false);
            setVerifyingCodeSuccess(false);
            setSendingCodeError(false);
            setVerifyingCodeError(true);
            changeVerificationStatus(false);
          });
      },
    },
    {
      text: inputLabels ? inputLabels.resendLink : '',
      onClick: () => {
        setVerifyingCodeError(false);
        dispatchSendCode();
      },
    },
  ];

  const jsxVerifyCodeFormModal = (
    <Modal title={inputLabels?.codeSent} onClose={() => setModalOpen(false)} buttons={buttonsVerifyCodeForm}>
      <FormGroup
        id="codeVerification"
        label={inputLabels ? inputLabels.codeInput : ''}
        errorMessage={inputLabels ? inputLabels.invalidCode : ''}
        hasError={errorVerifyingCode}
      >
        <Input
          placeholder={inputLabels ? inputLabels.codeInput : ''}
          value={inputCode}
          onChange={(evt) => {
            setInputCode(evt.target.value);
          }}
        />
        {/*Do not remove empty p tag, it is to fix the space issue*/}
        <p>&nbsp;</p>
      </FormGroup>
    </Modal>
  );

  const jsxSendingCodeLoader = <ComponentLoader label={`Sending code to ${inputValue}...`} fullPage={true} />;

  const jsxVerifyingCodeLoader = <ComponentLoader label={'Verifying code...'} fullPage={true} />;

  const dispatchSendCode = () => {
    setSendingCode(true);
    setSendingCodeSuccess(false);
    setSendingCodeError(false);
    setInputCode('');
    setModalOpen(true);

    EmailMobileVerificationService.sendCode(baseUrl, sendCodePath, inputValue, inputType)
      .then(() => {
        setModalOpen(true);
        setSendingCode(false);
        setSendingCodeSuccess(true);
        setSendingCodeError(false);
      })
      .catch(() => {
        setModalOpen(true);
        setSendingCode(false);
        setSendingCodeSuccess(false);
        setSendingCodeError(true);
      });
  };

  const validateInputValue = (value) => {
    const isValid = config.type === 'mobile' ? isValidMobile(value) : isValidEmail(value);
    setIsInputValid(isValid);
    return isValid;
  };
  const jsxVerifyButton = (
    <Button
      variant={'secondary'}
      onClick={() => {
        const isValid = validateInputValue(inputValue);
        if (isValid) {
          dispatchSendCode();
        }
      }}
    >
      {inputLabels?.verifyButton}
    </Button>
  );

  const onInputChange = (value) => {
    setInputValue(value);

    //only check validations, when button is hidden due to Verified status
    if (isVerifiedOnce) {
      validateInputValue(value);
    }

    //call verify event from props
    changeVerificationStatus(value != '' && value == verifiedInputValue ? true : false);

    //call onChange event from props
    if (props.onEmailMobileChange) {
      props.onEmailMobileChange(value);
    }
  };

  return (
    <div className={styles.EmailMobileVerification}>
      <Row className={styles.inputContainer + ' ' + (config?.helpMessage ? styles.helpMessage : '')}>
        <Col className="" span={8}>
          <FormInput
            placeholder={inputLabels?.label}
            label={inputLabels?.label}
            value={inputValue}
            errorMessage={!isInputValid ? inputLabels?.notValid : !isInputVerified ? inputLabels?.notVerified : ''}
            hasError={!isInputValid || (isVerifiedOnce && !isInputVerified)}
            helpMessage={config.helpMessage}
            prefix={config.type !== 'mobile' ? undefined : '+61'}
            onChange={(evt) => {
              onInputChange(evt.target.value);
            }}
          />
        </Col>
        <Col className="" span={4}>
          {!isInputVerified && jsxVerifyButton}
          {isInputVerified && <img src={Success} />}
        </Col>
      </Row>
      {sendingCode && !sendingCodeError && jsxSendingCodeLoader}
      {isModalOpen && sendingCodeError && jsxSendingCodeErrorModal}
      {isModalOpen && sendingCodeSuccess && jsxVerifyCodeFormModal}
    </div>
  );
};

export default forwardRef(EmailMobileVerification);
